import { getUserRoutes, getUserPermissions, getRouteTree } from '@/api/system/auth/dynamicRoute'
import DynamicRouteUtils from '@/utils/dynamicRoute'
import { constantRoutes, addDynamicRoutes } from '@/router'
import Layout from '@/layout'

const state = {
  // 路由相关状态
  routes: [],
  addRoutes: [],
  permissions: [],

  // 系统路由缓存
  oaRoutes: [],
  labRoutes: [],
  baseRoutes: [],
  dataRoutes: [],

  // 路由加载状态
  routesLoaded: false,
  loading: false,

  // 当前系统
  currentSystem: 'oa'
}

const mutations = {
  SET_ROUTES: (state, routes) => {
    state.addRoutes = routes
    state.routes = constantRoutes.concat(routes)
  },

  SET_PERMISSIONS: (state, permissions) => {
    state.permissions = permissions
  },

  SET_OA_ROUTES: (state, routes) => {
    state.oaRoutes = routes
  },

  SET_LAB_ROUTES: (state, routes) => {
    state.labRoutes = routes
  },

  SET_BASE_ROUTES: (state, routes) => {
    state.baseRoutes = routes
  },
  SET_DATA_ROUTES: (state, routes) => {
    state.dataRoutes = routes
  },

  SET_ROUTES_LOADED: (state, loaded) => {
    state.routesLoaded = loaded
  },

  SET_LOADING: (state, loading) => {
    state.loading = loading
  },

  SET_CURRENT_SYSTEM: (state, system) => {
    state.currentSystem = system
  },

  CLEAR_ROUTES: (state) => {
    state.routes = []
    state.addRoutes = []
    state.permissions = []
    state.oaRoutes = []
    state.labRoutes = []
    state.baseRoutes = []
    state.dataRoutes = []
    state.routesLoaded = false
    state.currentSystem = 'oa'
  }
}

const actions = {
  /**
   * 生成路由
   * @param {Object} context - Vuex context
   * @param {string} system - 系统标识
   */
  async generateRoutes({ commit, dispatch }, system = 'oa') {
    commit('SET_LOADING', true)

    try {
      // 获取用户权限
      await dispatch('fetchUserPermissions')

      // 根据系统获取路由
      let routes = []
      switch (system) {
        case 'oa':
          routes = await dispatch('fetchOaRoutes')
          break
        case 'lab':
          routes = await dispatch('fetchLabRoutes')
          break
        case 'base':
          routes = await dispatch('fetchBaseRoutes')
          break
        case 'data':
          routes = await dispatch('fetchDataRoutes')
          break
        default:
          routes = await dispatch('fetchOaRoutes')
      }

      commit('SET_CURRENT_SYSTEM', system)
      commit('SET_ROUTES', routes)
      commit('SET_ROUTES_LOADED', true)

      // 将路由添加到路由实例
      addDynamicRoutes(routes)

      return routes
    } catch (error) {
      console.error('生成路由失败:', error)
      throw error
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 获取用户权限
   */
  async fetchUserPermissions({ commit }) {
    try {
      const response = await getUserPermissions()
      const permissions = response.data || []
      commit('SET_PERMISSIONS', permissions)
      return permissions
    } catch (error) {
      console.error('获取用户权限失败:', error)
      commit('SET_PERMISSIONS', [])
      return []
    }
  },

  /**
   * 获取OA系统路由
   */
  async fetchOaRoutes({ commit, state }) {
    // 如果已经加载过，直接返回缓存
    if (state.oaRoutes.length > 0) {
      return state.oaRoutes
    }

    try {
      const response = await getUserRoutes({ sysKey: 'oa' })
      const backendRoutes = response.data || []

      // 过滤有效路由
      const validRoutes = DynamicRouteUtils.filterValidRoutes(backendRoutes)

      // 转换为前端路由格式
      const frontendRoutes = DynamicRouteUtils.transformRoutes(validRoutes)

      // 添加OA欢迎页路由
      const oaRoutes = [
        {
          path: '/oa',
          component: Layout,
          redirect: '/oa/welcome',
          children: [
            ...frontendRoutes
          ]
        },
        {
          path: '*',
          redirect: '/404',
          hidden: true
        }
      ]

      commit('SET_OA_ROUTES', oaRoutes)
      return oaRoutes
    } catch (error) {
      console.error('获取OA路由失败:', error)
      const fallbackRoutes = [{
        path: '/oa',
        component: Layout,
        redirect: '/oa/welcome',
        children: [{
          path: '/oa/welcome',
          component: () => import('@/views/dashboard/oaWelcome'),
          name: 'OaWelcome',
          meta: { title: 'OA首页', icon: 'dashboard', affix: true }
        }]
      }]
      commit('SET_OA_ROUTES', fallbackRoutes)
      return fallbackRoutes
    }
  },

  /**
   * 获取实验室系统路由
   */
  async fetchLabRoutes({ commit, state }) {
    if (state.labRoutes.length > 0) {
      return state.labRoutes
    }

    try {
      const response = await getUserRoutes({ sysKey: 'lab' })
      const backendRoutes = response.data || []

      const validRoutes = DynamicRouteUtils.filterValidRoutes(backendRoutes)
      const frontendRoutes = DynamicRouteUtils.transformRoutes(validRoutes)

      const labRoutes = [
        {
          path: '/lab',
          component: Layout,
          redirect: '/lab/welcome',
          children: [
            ...frontendRoutes
          ]
        },
        {
          path: '*',
          redirect: '/404',
          hidden: true
        }
      ]

      commit('SET_LAB_ROUTES', labRoutes)
      return labRoutes
    } catch (error) {
      console.error('获取实验室路由失败:', error)
      const fallbackRoutes = [{
        path: '/lab',
        component: Layout,
        redirect: '/lab/welcome',
        children: [{
          path: '/lab/welcome',
          component: () => import('@/views/dashboard/labWelcome'),
          name: 'LabWelcome',
          meta: { title: '实验室首页', icon: 'dashboard', affix: true }
        }]
      }]
      commit('SET_LAB_ROUTES', fallbackRoutes)
      return fallbackRoutes
    }
  },

  /**
   * 获取基础系统路由
   */
  async fetchBaseRoutes({ commit, state }) {
    if (state.baseRoutes.length > 0) {
      return state.baseRoutes
    }

    try {
      const response = await getUserRoutes({ sysKey: 'base' })
      const backendRoutes = response.data || []

      const validRoutes = DynamicRouteUtils.filterValidRoutes(backendRoutes)
      const frontendRoutes = DynamicRouteUtils.transformRoutes(validRoutes)

      const baseRoutes = [
        {
          path: '/base',
          component: Layout,
          redirect: '/base/welcome',
          children: [
            {
              path: '/base/welcome',
              component: () => import('@/views/dashboard/index'),
              name: 'BaseWelcome',
              meta: {
                title: '系统管理首页',
                icon: 'dashboard',
                affix: true
              }
            },
            ...frontendRoutes
          ]
        },
        {
          path: '*',
          redirect: '/404',
          hidden: true
        }
      ]

      commit('SET_BASE_ROUTES', baseRoutes)
      return baseRoutes
    } catch (error) {
      console.error('获取基础系统路由失败:', error)
      const fallbackRoutes = [{
        path: '/base',
        component: Layout,
        redirect: '/base/welcome',
        children: [{
          path: '/base/welcome',
          component: () => import('@/views/dashboard/index'),
          name: 'BaseWelcome',
          meta: { title: '系统管理首页', icon: 'dashboard', affix: true }
        }]
      }]
      commit('SET_BASE_ROUTES', fallbackRoutes)
      return fallbackRoutes
    }
  },

  async fetchDataRoutes({ commit, state }) {
    if (state.dataRoutes.length > 0) {
      return state.dataRoutes
    }

    try {
      const response = await getUserRoutes({ sysKey: 'data' })
      const backendRoutes = response.data || []

      const validRoutes = DynamicRouteUtils.filterValidRoutes(backendRoutes)
      const frontendRoutes = DynamicRouteUtils.transformRoutes(validRoutes)

      const dataRoutes = [
        {
          path: '/data',
          component: Layout,
          redirect: '/data/welcome',
          children: [
            {
              path: '/data/welcome',
              component: () => import('@/views/dashboard/dataWelcome'),
              name: 'DataWelcome',
              meta: {
                title: '实验室统计分析首页',
                icon: 'dashboard',
                affix: true
              }
            },
            ...frontendRoutes
          ]
        },
        {
          path: '*',
          redirect: '/404',
          hidden: true
        }
      ]

      commit('SET_DATA_ROUTES', dataRoutes)
      return dataRoutes
    } catch (error) {
      console.error('获取实验室统计分析系统路由失败:', error)
      const fallbackRoutes = [{
        path: '/data',
        component: Layout,
        redirect: '/data/welcome',
        children: [{
          path: '/data/welcome',
          component: () => import('@/views/dashboard/dataWelcome'),
          name: 'DataWelcome',
          meta: { title: '实验室统计分析', icon: 'dashboard', affix: true }
        }]
      }]
      commit('SET_DATA_ROUTES', fallbackRoutes)
      return fallbackRoutes
    }
  },

  /**
   * 切换系统
   */
  async switchSystem({ dispatch, commit }, system) {
    commit('SET_LOADING', true)

    try {
      const routes = await dispatch('generateRoutes', system)
      return routes
    } finally {
      commit('SET_LOADING', false)
    }
  },

  /**
   * 刷新路由
   */
  async refreshRoutes({ commit, dispatch, state }) {
    // 清除缓存
    commit('CLEAR_ROUTES')

    // 重新生成路由
    return await dispatch('generateRoutes', state.currentSystem)
  },

  /**
   * 检查路由权限
   */
  hasRoutePermission({ state }, routePath) {
    if (!routePath) return true

    // 白名单路由直接允许访问
    const whiteList = ['/', '/welcome', '/login', '/auth-redirect', '/404', '/401', '/m/sample-registration']
    if (whiteList.includes(routePath)) {
      return true
    }

    // 如果没有权限数据，默认允许访问
    if (!state.permissions || state.permissions.length === 0) {
      return true
    }

    // 查找路由对应的权限
    const flatRoutes = DynamicRouteUtils.flattenRoutes(state.routes)
    const route = flatRoutes.find(r => r.path === routePath || r.fullPath === routePath)

    if (!route || !route.funcPerms) {
      return true // 没有权限要求的路由默认允许访问
    }

    return state.permissions.includes(route.funcPerms)
  }
}

const getters = {
  routes: state => state.routes,
  addRoutes: state => state.addRoutes,
  permissions: state => state.permissions,
  routesLoaded: state => state.routesLoaded,
  loading: state => state.loading,
  currentSystem: state => state.currentSystem,

  // 获取各系统路由
  oaRoutes: state => state.oaRoutes,
  labRoutes: state => state.labRoutes,
  baseRoutes: state => state.baseRoutes,
  dataRoutes: state => state.dataRoutes,

  // 获取当前系统的路由
  currentRoutes: state => {
    switch (state.currentSystem) {
      case 'oa':
        return state.oaRoutes
      case 'lab':
        return state.labRoutes
      case 'base':
        return state.baseRoutes
      case 'data':
        return state.dataRoutes
      default:
        return state.oaRoutes
    }
  },

  // 获取侧边栏菜单
  sidebarRoutes: (state, getters) => {
    return getters.currentRoutes.filter(route => !route.hidden)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}
